import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { getSupabase } from "@/lib/supabase-lazy";
import { getPrefs } from "@/lib/prefs.functions";
import { syncEarlyAccess } from "@/lib/early-access.functions";
import { setAccessibilityDisabled, isAccessibilityDisabled } from "@/components/AccessibilityMenu";

/**
 * Beta/early-access seats are stamped onto the profile by syncEarlyAccess.
 * Sign-in via password calls it directly, but users who land here straight
 * from a confirmation email or magic link never pass through that branch —
 * so run it once per browser session for every authenticated entry point.
 * The server fn is idempotent and never resurrects a seat an admin ended.
 */
function CohortSync() {
  const queryClient = useQueryClient();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (sessionStorage.getItem("cohort-synced") === "1") return;
    void (async () => {
      try {
        const res = await syncEarlyAccess({});
        sessionStorage.setItem("cohort-synced", "1");
        if (res?.ok) {
          await queryClient.invalidateQueries({ queryKey: ["account"] });
        }
      } catch {
        /* non-blocking */
      }
    })();
  }, [queryClient]);

  return null;
}


/**
 * The accessibility preference lives on the account, so every authenticated
 * screen (not just the /app shell) has to honour it — otherwise routes like
 * /admin show the floating button again on a fresh device or cleared storage.
 */
function AccountPrefsSync() {
  const prefs = useQuery({
    queryKey: ["prefs"],
    queryFn: () => getPrefs(),
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => {
    // Persisted query data may be from an older app session. Keep the local
    // device mirror in force until a fresh account read completes.
    if (!prefs.data || prefs.isFetching) return;
    const wanted = Boolean(prefs.data.a11yDisabled);
    if (wanted !== isAccessibilityDisabled()) setAccessibilityDisabled(wanted);
  }, [prefs.data, prefs.isFetching]);

  return null;
}

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    const supabase = await getSupabase();
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });
    return { user: data.user };
  },
  component: () => (
    <>
      <AccountPrefsSync />
      <CohortSync />

      <Outlet />
    </>
  ),
});
